// src/components/auth/ProtectedRoute.js
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../common/Loadingspinner';
import { Box, Alert } from '@mui/material';

const ProtectedRoute = ({ 
  children, 
  requiredRole = null, 
  requiredCompanyAccess = null,
  fallbackPath = '/login' 
}) => {
  const { isAuthenticated, isLoading, user, hasRole, canAccessCompany } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  // Check role-based access
  if (requiredRole && !hasRole(requiredRole)) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Access Denied: You don't have permission to access this page. 
          Required role: {requiredRole}, Your role: {user?.role}
        </Alert>
      </Box>
    );
  }

  // Check company-based access
  if (requiredCompanyAccess && !canAccessCompany(requiredCompanyAccess)) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Access Denied: You don't have permission to access this company's data.
        </Alert>
      </Box>
    );
  }

  // User is authenticated and authorized
  return children;
};

// Higher-order component for role-based protection
export const withRoleProtection = (Component, requiredRole) => {
  return function ProtectedComponent(props) {
    return (
      <ProtectedRoute requiredRole={requiredRole}>
        <Component {...props} />
      </ProtectedRoute>
    );
  };
};

// Higher-order component for company-based protection
export const withCompanyProtection = (Component, getCompanyId) => {
  return function ProtectedComponent(props) {
    const companyId = typeof getCompanyId === 'function' ? getCompanyId(props) : getCompanyId;
    
    return (
      <ProtectedRoute requiredCompanyAccess={companyId}>
        <Component {...props} />
      </ProtectedRoute>
    );
  };
};

export default ProtectedRoute;